import { api } from '@/services/api';
import * as AuthService from '@/services/auth';
import * as ChatService from '@/services/chat';
import * as ConversationsService from '@/services/conversations';
import SettingsService from '@/services/settings';
import UserService from '@/services/user';

import {
  IChatService,
  IAuthService,
  IConversationsService,
  ISettingsService,
  IUserService,
  Services,
} from './serviceTypes';

/**
 * Creates an IChatService instance that provides conversation management methods.
 *
 * Chat messaging is handled via WebSocket (see websocketService.ts); this factory
 * only wraps conversation creation. Falls back to a test-safe stub if the underlying
 * ChatService module is unavailable.
 *
 * @returns An object implementing IChatService with a `createNewConversation` method.
 */
function createChatService(): IChatService {
  // Safe access to ChatService with fallbacks
  const chatServiceInstance = ChatService.default || ChatService;

  return {
    createNewConversation: async () => {
      if (chatServiceInstance && typeof chatServiceInstance.createNewConversation === 'function') {
        return chatServiceInstance.createNewConversation.call(chatServiceInstance);
      }
      // Fallback for tests
      return { id: 'test-conversation' };
    },
  };
}

/**
 * Creates an IAuthService instance wrapping the authentication module.
 *
 * Delegates to the AuthService functions (login, logout, token management,
 * remember-me). Each method falls back to a no-op if the underlying function
 * is not available.
 *
 * @returns An object implementing IAuthService.
 */
function createAuthService(): IAuthService {
  return {
    login: AuthService.login || (() => Promise.resolve()),
    logout: AuthService.logout || (() => Promise.resolve()),
    isRememberMeEnabled: AuthService.isRememberMeEnabled || (() => false),
    setToken: AuthService.setToken || (() => {}),
    getToken: AuthService.getToken || (() => null),
    removeToken: AuthService.removeToken || (() => {}),
  };
}

/**
 * Creates an IConversationsService instance wrapping conversation CRUD operations.
 *
 * Provides methods for listing all conversations and deleting a conversation,
 * with no-op fallbacks for test environments.
 *
 * @returns An object implementing IConversationsService.
 */
function createConversationsService(): IConversationsService {
  return {
    getAllConversations: ConversationsService.getAllConversations || (() => Promise.resolve([])),
    deleteConversation: ConversationsService.deleteConversation || (() => Promise.resolve()),
  };
}

/**
 * Creates an ISettingsService instance wrapping user settings operations.
 *
 * Binds the `getAllSettings` and `updateSetting` methods to the SettingsService
 * singleton so they retain the correct `this` context when invoked.
 *
 * @returns An object implementing ISettingsService.
 */
function createSettingsService(): ISettingsService {
  return {
    getAllSettings:
      SettingsService.getAllSettings.bind(SettingsService) || (() => Promise.resolve({})),
    updateSetting: SettingsService.updateSetting.bind(SettingsService) || (() => Promise.resolve()),
  };
}

/**
 * Creates an IUserService instance wrapping user profile operations.
 *
 * Provides a `getCurrentUser` method bound to the UserService singleton.
 *
 * @returns An object implementing IUserService.
 */
function createUserService(): IUserService {
  return {
    getCurrentUser: UserService.getCurrentUser.bind(UserService) || (() => Promise.resolve(null)),
  };
}

/**
 * Factory function that creates and returns all application service instances.
 *
 * Assembles the complete Services object by invoking each individual service
 * factory (chat, auth, conversations, settings, user) and including the
 * shared API client. This is the single entry point for dependency injection
 * of services into the React context.
 *
 * @returns A Services object containing all service instances and the API client.
 */
export function createServices(): Services {
  return {
    chatService: createChatService(),
    authService: createAuthService(),
    conversationsService: createConversationsService(),
    settingsService: createSettingsService(),
    userService: createUserService(),
    api,
  };
}
